import type { ApprovalException } from '@my/tms-api';

/**
 * Human label for an approval exception. POD / charges are fixed; reference
 * checks use the org's custom reference label (from the org config), falling
 * back to "Reference N". Shared by the For-approval grid badges and the MyI
 * summary so they never drift.
 */
export function exceptionLabel(
  e: Pick<ApprovalException, 'code' | 'referenceSlot'>,
  refLabels: string[],
): string {
  if (e.code === 'pod') return 'No POD uploaded';
  if (e.code === 'charges') return 'No charges set';
  if (e.referenceSlot) {
    const label = refLabels[e.referenceSlot - 1] || `Reference ${e.referenceSlot}`;
    return `No ${label}`;
  }
  return e.code;
}

/** Rank for ordering by severity: a Stop always outranks a Warn. */
export const SEVERITY_RANK: Record<string, number> = { Stop: 2, Warn: 1, Ignore: 0 };

function codeRank(code: string): number {
  if (code === 'charges') return 3; // blocks billing
  if (code === 'pod') return 2;
  return 1; // reference{n}
}

/**
 * The single most-important exception in a set — Stop outranks Warn; within a
 * severity, charges > POD > reference. Used both for the row badge and to
 * bucket an order under one issue so the MyI breakdown partitions cleanly.
 */
export function pickPrimaryException<T extends Pick<ApprovalException, 'code' | 'severity'>>(
  exceptions: T[],
): T | null {
  if (exceptions.length === 0) return null;
  return [...exceptions].sort((a, b) => {
    const bySeverity = (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0);
    return bySeverity !== 0 ? bySeverity : codeRank(b.code) - codeRank(a.code);
  })[0]!;
}
